/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   isValidBounds
 */

import { isNumber } from '@zcode/utils'
import QuickLRU from 'quick-lru'
import type { AutocompleteBoxType, BoundsObj, BoxTuple, MaybeNullBoundsObj } from '../../types'
import { getQueryBounds } from '../location'
import { hashCode } from '../utils'

const checkedBoundsMap = new QuickLRU<number, boolean>({ maxSize: 100 })

export const isValidBounds = <
  T extends
    | AutocompleteBoxType
    | MaybeNullBoundsObj
    | BoxTuple
    | readonly number[]
    | null
    | undefined,
>(
  bounds: T
): bounds is T &
  (T extends { length: number } ? readonly [number, number, number, number] : BoundsObj) => {
  if (!bounds) {
    return false
  }
  // first check if we have already seen this bounds
  const key = hashCode(JSON.stringify(bounds))
  const cached = checkedBoundsMap.get(key)
  if (typeof cached === 'boolean') {
    return cached
  }

  const { maxLat, maxLng, minLat, minLng } = getQueryBounds(bounds) || {}
  let isValid = true

  // we can build this up as we going forward
  if (!isNumber(maxLat) || !isNumber(maxLng) || !isNumber(maxLng) || !isNumber(minLng)) {
    isValid = false
  } else if (maxLng === minLng || maxLat === minLat) {
    isValid = false
  }
  checkedBoundsMap.set(key, isValid)
  return isValid
}
